/**
 * ReportsPage — 报告中心（Issue #227）。
 *
 * 真相边界：
 *   - 仅展示当前版本已落地的"报告/导出"能力的真实导航入口、真实异步导出任务列表
 *     （来自 `/api/v1/dashboard/exports`，与 Dashboard 一致），以及明确标注为
 *     "暂未实现/待 Issue 落地"的占位条目。
 *   - 禁止伪造任何统计、计数、状态、报表数据或审批流；缺契约的能力必须显示
 *     "暂未实现"，由后续 Issue 单独落地。
 *   - 所有导航条目按当前会话权限过滤；无权限的入口直接隐藏或显示受控提示。
 *
 * 这个页面不创建新的后端契约；其异步导出区块复用 Dashboard Issue #189 已落地的
 * 端点和 hook，参数 JSON 仍按既有契约要求脱敏（不得包含 PII/PHI/价格明细）。
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactElement,
} from 'react';
import { Link } from 'wouter';
import {
  Activity,
  ClipboardList,
  FileText,
  FolderInput,
  ShieldCheck,
  Users,
  Boxes,
  ScanLine,
  Bot,
  MessageSquare,
  MessageSquareWarning,
  IdCard,
  LineChart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useExportTasks } from '@/hooks/useDashboard';
import { canSeeCrmMenu, hasAnyPermission, hasPermission } from '@/lib/crmPermissions';
import { canSeeComplianceMenu } from '@/lib/compliancePermissions';
import { canSeeCustomerDocumentMenu } from '@/lib/customerDocumentPermissions';
import { canSeeHealthReportPublicationMenu } from '@/lib/healthReportPublicationPermissions';
import { canSeeComplaintMenu } from '@/lib/complaintPermissions';
import { canSeeHrMenu } from '@/lib/hrPermissions';
import { canSeeWorkOrderMenu } from '@/lib/swoPermissions';
import { canSeeAiModelCenterMenu } from '@/lib/aiModelCenterPermissions';
import { canSeeSmsAdminMenu } from '@/lib/smsPermissions';
import { canSeeFinanceMenu } from '@/lib/financePermissions';
import { canSeeSalesFunnelChannelRoiSummary } from '@/lib/reportsPermissions';
import type { CreateExportTaskRequest, ExportTaskDto } from '@jinzhuan/api-client';

const DASHBOARD_READ = 'dashboard:read';
const DASHBOARD_EXPORT = 'dashboard:export';

type ExportTypeKey = CreateExportTaskRequest['exportType'];

const EXPORT_TYPE_LABELS: Record<string, string> = {
  golden_analysis: '黄金分析表',
  deal_intent: '成交意向表',
  proposal_quote: '方案报价单',
  payment_voucher: '付款单',
  lab_batch_record: '实验室批记录',
  qc_report: '质检报告',
  infusion_record: '回输记录',
  followup_report: '随访报告',
};

const EXPORT_STATUS_LABELS: Record<string, string> = {
  pending: '排队中',
  running: '处理中',
  succeeded: '已完成',
  failed: '失败',
  expired: '已过期',
  cancelled: '已取消',
};

interface ReportLinkSpec {
  readonly to: string;
  readonly label: string;
  readonly description: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly visible: (permissions: readonly string[]) => boolean;
}

const REPORT_LINKS: readonly ReportLinkSpec[] = [
  {
    to: '/',
    label: '运营看板',
    description: '跨模块汇总指标、收益三阶段（待解锁/已解锁/已支付核销）和异步导出。',
    icon: LineChart,
    visible: (perms) => hasPermission(perms, DASHBOARD_READ),
  },
  {
    to: '/crm/customers',
    label: '客户管理',
    description: '客户、阶段、跟进、家庭订单/团单的列表与详情入口。',
    icon: Users,
    visible: canSeeCrmMenu,
  },
  {
    to: '/finance/payment-records',
    label: '财务流水与项目解锁',
    description: '付款记录、项目级解锁、收益分账、提成分账的查询入口。',
    icon: FileText,
    visible: canSeeFinanceMenu,
  },
  {
    to: '/reports/sales-funnel-channel-roi-summary',
    label: '销售漏斗与渠道 ROI 汇总',
    description:
      '按客户来源、阶段转化、渠道投入、订单成交与回款、提成 / 结算状态汇总的 read-model（Issue #397）。',
    icon: LineChart,
    visible: canSeeSalesFunnelChannelRoiSummary,
  },
  {
    to: '/operations/work-orders',
    label: '服务工单',
    description: '采集、检测、制备、质检、回输、随访等服务工单的列表与详情。',
    icon: ClipboardList,
    visible: canSeeWorkOrderMenu,
  },
  {
    to: '/lab/batches',
    label: '实验室批次与质检',
    description: '细胞批次、质检工单、放行单、回输单、样本记录、异常事件查询。',
    icon: Boxes,
    visible: (perms) =>
      hasAnyPermission(perms, [
        'lab.cell_batch:read',
        'lab.qc_order:read',
        'lab.release_order:read',
        'lab.infusion:read',
      ]),
  },
  {
    to: '/compliance',
    label: '政策合规',
    description: '政策文件、项目合规状态、宣传审核、敏感词与复核任务查询。',
    icon: ShieldCheck,
    visible: canSeeComplianceMenu,
  },
  {
    to: '/customer-documents',
    label: '客户资料审核记录',
    description: '客户上传资料的审核队列、审核记录与历史。',
    icon: FolderInput,
    visible: canSeeCustomerDocumentMenu,
  },
  {
    to: '/health-report-publications',
    label: '健康趋势/AI 报告发布',
    description: '医生、技术负责人、合规审核后的客户可见健康报告发布记录。',
    icon: Activity,
    visible: canSeeHealthReportPublicationMenu,
  },
  {
    to: '/complaints',
    label: '投诉与纠纷工单',
    description: '投诉受理、流转、升级、复核和关闭工单的列表与详情。',
    icon: MessageSquareWarning,
    visible: canSeeComplaintMenu,
  },
  {
    to: '/hr/employees',
    label: '员工花名册',
    description: '员工导入、考勤同步、候选人、面试与培训记录。',
    icon: IdCard,
    visible: canSeeHrMenu,
  },
  {
    to: '/scan',
    label: '统一扫码',
    description: '扫码解析与跨模块对象路由（含身份核对入口）。',
    icon: ScanLine,
    visible: (perms) => hasPermission(perms, 'scan:resolve'),
  },
  {
    to: '/ai-model-center',
    label: 'AI 模型中心',
    description: 'AI 模型、凭证与调用审计；客户可见 AI 内容须经多方审核。',
    icon: Bot,
    visible: canSeeAiModelCenterMenu,
  },
  {
    to: '/sms',
    label: '短信模板与日志',
    description: '短信模板和发送日志；展示号码掩码，不展示验证码原文。',
    icon: MessageSquare,
    visible: canSeeSmsAdminMenu,
  },
];

interface MissingContractItem {
  readonly key: string;
  readonly label: string;
  readonly reason: string;
}

const MISSING_CONTRACT_ITEMS: readonly MissingContractItem[] = [
  {
    key: 'custom_report_designer',
    label: '自定义报表设计器',
    reason: '后端暂未提供报表模板/字段映射 API，待后续 Issue 拆分。',
  },
  {
    key: 'lab_analytics_report',
    label: '实验室质检/放行/异常事件汇总报表',
    reason: '已有列表查询入口；专项汇总报表需后续 read-model Issue。',
  },
  {
    key: 'finance_settlement_report',
    label: '财务结算/对账/发票汇总报表',
    reason: '付款流水与解锁明细已可查；结算和发票汇总报表待财务 Issue 落地。',
  },
  {
    key: 'followup_outcome_report',
    label: '随访与疗效不达预期汇总报表',
    reason: '随访明细在 CRM/服务工单中可查；汇总报表待后续 Issue。',
  },
];

export default function ReportsPage(): ReactElement {
  const { permissions } = useAuth();
  const canExport = hasPermission(permissions, DASHBOARD_EXPORT);

  const visibleLinks = useMemo(
    () => REPORT_LINKS.filter((l) => l.visible(permissions)),
    [permissions],
  );

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header>
        <h2 className="text-2xl font-semibold">报告中心</h2>
        <p className="text-sm text-muted-foreground">
          本页面只展示当前版本已落地的报表/导出能力的真实入口、真实异步导出任务，以及
          明确标注的"待 Issue 落地"占位。所有数据来自各业务模块的 read-model，不展示前端伪造的统计或状态。
        </p>
      </header>

      <section className="space-y-3" data-testid="reports-modules-section">
        <div>
          <h3 className="text-lg font-medium">已落地的模块查询入口</h3>
          <p className="text-sm text-muted-foreground">
            根据当前账号的权限显示。无权限模块不会出现在下方列表中。
          </p>
        </div>
        {visibleLinks.length === 0 ? (
          <Card>
            <CardContent>
              <div
                className="rounded border border-dashed py-8 text-center text-sm text-muted-foreground"
                data-testid="reports-modules-empty"
              >
                当前账号未授予任何报表/查询权限。请联系管理员分配模块权限。
              </div>
            </CardContent>
          </Card>
        ) : (
          <div
            className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
            data-testid="reports-modules-grid"
          >
            {visibleLinks.map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.to}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <Icon className="h-4 w-4" aria-hidden />
                      {item.label}
                    </CardTitle>
                    <CardDescription>{item.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link
                      href={item.to}
                      className="text-sm text-primary underline-offset-2 hover:underline"
                      data-testid={`reports-module-link-${item.to}`}
                    >
                      进入模块
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="reports-exports-section">
        <div>
          <h3 className="text-lg font-medium">异步导出任务</h3>
          <p className="text-sm text-muted-foreground">
            通过后端 `/api/v1/dashboard/exports` 创建并查询；下载链接由后端短期签发，不缓存。
          </p>
        </div>
        {canExport ? (
          <ExportTasksPanel />
        ) : (
          <Card>
            <CardContent>
              <div
                className="rounded border border-dashed py-8 text-center text-sm text-muted-foreground"
                data-testid="reports-exports-no-permission"
              >
                当前账号缺少 {DASHBOARD_EXPORT} 权限，无法查看或创建异步导出任务。
              </div>
            </CardContent>
          </Card>
        )}
      </section>

      <section className="space-y-3" data-testid="reports-missing-section">
        <div>
          <h3 className="text-lg font-medium">待 Issue 落地的报表</h3>
          <p className="text-sm text-muted-foreground">
            以下报表/分析能力当前版本尚未提供后端契约，按金专 AI GUIDE 规定，
            禁止在此处编写假业务状态或假统计。具体落地由各模块 Issue 单独承接。
          </p>
        </div>
        <Card>
          <CardContent className="space-y-2 py-4">
            {MISSING_CONTRACT_ITEMS.map((item) => (
              <div
                key={item.key}
                className="rounded border border-dashed p-3 text-sm"
                data-testid={`reports-missing-${item.key}`}
              >
                <div className="font-medium">{item.label}</div>
                <div className="text-xs text-muted-foreground">{item.reason}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function ExportTasksPanel(): ReactElement {
  const { tasks, loading, error, fetchTasks, createTask, cancelTask, requestDownloadUrl } =
    useExportTasks();
  const [exportType, setExportType] = useState<ExportTypeKey>('golden_analysis');
  const [parameterText, setParameterText] = useState<string>('{"dateRange":["2026-05-01","2026-05-19"]}');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    void fetchTasks({ limit: 20 });
  }, [fetchTasks]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onCreate = useCallback(async () => {
    setSubmitError(null);
    let parsed: Record<string, unknown> = {};
    if (parameterText.trim().length > 0) {
      try {
        parsed = JSON.parse(parameterText) as Record<string, unknown>;
      } catch {
        setSubmitError('参数 JSON 格式不正确');
        return;
      }
    }
    const created = await createTask({ exportType, parameterSummary: parsed });
    if (created) {
      refresh();
    }
  }, [createTask, exportType, parameterText, refresh]);

  const onCancel = useCallback(
    async (task: ExportTaskDto) => {
      const reason = window.prompt('取消原因（4..255 字符）');
      if (!reason) return;
      const cancelled = await cancelTask(task.id, {
        expectedVersion: task.version,
        reason,
      });
      if (cancelled) {
        refresh();
      }
    },
    [cancelTask, refresh],
  );

  const onDownload = useCallback(
    async (task: ExportTaskDto) => {
      setDownloadError(null);
      const result = await requestDownloadUrl(task.id, {});
      if (!result) {
        setDownloadError('无法获取下载链接，请稍后重试或刷新任务列表。');
        return;
      }
      window.open(result.url, '_blank', 'noopener,noreferrer');
    },
    [requestDownloadUrl],
  );

  const isDownloadable = useCallback((task: ExportTaskDto): boolean => {
    if (task.status !== 'succeeded') return false;
    if (!task.expiresAt) return true;
    return new Date(task.expiresAt).getTime() > Date.now();
  }, []);

  const orderedTypes = useMemo<readonly ExportTypeKey[]>(
    () =>
      [
        'golden_analysis',
        'deal_intent',
        'proposal_quote',
        'payment_voucher',
        'lab_batch_record',
        'qc_report',
        'infusion_record',
        'followup_report',
      ] as const,
    [],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {loading ? '加载中…' : `共 ${tasks.length} 条任务（最近 20 条）`}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={refresh}
          disabled={loading}
          data-testid="reports-exports-refresh"
        >
          {loading ? '刷新中…' : '刷新'}
        </Button>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">创建导出任务</CardTitle>
          <CardDescription>
            参数 JSON 中严禁包含姓名/手机号/身份证/健康指标原文/价格明细。具体文件生成在后续
            Issue 落地，本任务首先记录请求。
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="reports-export-type">导出类型</Label>
            <select
              id="reports-export-type"
              data-testid="reports-export-type"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={exportType}
              onChange={(e) => setExportType(e.target.value as ExportTypeKey)}
            >
              {orderedTypes.map((t) => (
                <option key={t} value={t}>
                  {EXPORT_TYPE_LABELS[t]}（{t}）
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="reports-export-parameter">参数 JSON（已脱敏）</Label>
            <Input
              id="reports-export-parameter"
              data-testid="reports-export-parameter"
              value={parameterText}
              onChange={(e) => setParameterText(e.target.value)}
              placeholder='{"dateRange":["2026-05-01","2026-05-19"]}'
            />
          </div>
          {submitError && (
            <p className="text-sm text-destructive" data-testid="reports-export-submit-error">
              {submitError}
            </p>
          )}
          {error && (
            <p className="text-sm text-destructive" data-testid="reports-export-api-error">
              {error.message}
            </p>
          )}
          <Button
            onClick={() => void onCreate()}
            disabled={loading}
            data-testid="reports-export-submit"
          >
            提交任务
          </Button>
        </CardContent>
      </Card>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="reports-exports-table">
          <thead className="border-b">
            <tr className="text-xs uppercase text-muted-foreground">
              <th className="p-2">类型</th>
              <th className="p-2">状态</th>
              <th className="p-2">请求人</th>
              <th className="p-2">创建时间</th>
              <th className="p-2">行数</th>
              <th className="p-2">文件</th>
              <th className="p-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {tasks.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={7}
                  className="p-4 text-center text-muted-foreground"
                  data-testid="reports-exports-empty"
                >
                  当前 tenant/org 暂无导出任务记录。
                </td>
              </tr>
            )}
            {tasks.map((t) => (
              <tr key={t.id} className="border-b align-top last:border-0">
                <td className="p-2">
                  <div>{EXPORT_TYPE_LABELS[t.exportType] ?? t.exportType}</div>
                  <div className="text-xs text-muted-foreground">{t.exportType}</div>
                </td>
                <td className="p-2">
                  <span className="rounded bg-muted px-2 py-0.5 text-xs">
                    {EXPORT_STATUS_LABELS[t.status] ?? t.status}
                  </span>
                  {t.failureReason && (
                    <div className="mt-1 text-xs text-destructive">{t.failureReason}</div>
                  )}
                  {t.cancelReason && (
                    <div className="mt-1 text-xs text-muted-foreground">取消：{t.cancelReason}</div>
                  )}
                </td>
                <td className="p-2 text-xs text-muted-foreground">{t.requestedBy}</td>
                <td className="p-2 text-xs text-muted-foreground">
                  {new Date(t.createdAt).toLocaleString()}
                </td>
                <td className="p-2 text-xs">{t.rowCount ?? '—'}</td>
                <td className="p-2 text-xs">
                  {t.fileObjectKey ? (
                    <div className="text-muted-foreground">
                      已生成（{t.fileSizeBytes ?? '?'} 字节）；下载走单独签名接口
                    </div>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="space-x-2 p-2">
                  {(t.status === 'pending' || t.status === 'running') && (
                    <Button size="sm" variant="outline" onClick={() => void onCancel(t)}>
                      取消
                    </Button>
                  )}
                  {isDownloadable(t) && (
                    <Button size="sm" onClick={() => void onDownload(t)}>
                      下载
                    </Button>
                  )}
                  {t.status === 'succeeded' && !isDownloadable(t) && (
                    <span className="text-xs text-muted-foreground">已过期</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {downloadError && (
          <p className="mt-2 text-sm text-destructive" data-testid="reports-exports-download-error">
            {downloadError}
          </p>
        )}
      </div>
    </div>
  );
}
